/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Follow-up task at call wrap-up: what it saves, and when it reminds.
 *
 * Pure (no React, no axios) so it runs under CRA's jest. Due dates are stored
 * the way every other activity stores them, as local wall-clock time; the
 * reminder is an absolute instant (activities.reminder_at, timestamptz), so the
 * browser, which knows the agent's time zone, turns local due time into UTC.
 */

import { toLocalDateTimeApi } from "utils/DateTimeConversion";

/** Record types the console can link a call to, and their activity parent tables. */
export const PARENT_TABLES = {
  contact: "contacts",
  account: "accounts",
  lead: "leads",
  deal: "deals",
  case: "cases",
};

export const REMINDER_OPTIONS = [
  { value: "none", label: "No reminder" },
  { value: "at_due", label: "At the due time" },
  { value: "15m", label: "15 minutes before" },
  { value: "1h", label: "1 hour before" },
  { value: "1d", label: "1 day before" },
];

const OFFSET_MS = { at_due: 0, "15m": 15 * 60e3, "1h": 60 * 60e3, "1d": 24 * 60 * 60e3 };

const pad = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DDTHH:mm" for a datetime-local input, in the browser's zone. */
export function toInputValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Tomorrow at 10:00 local: a sensible default for "call them back". */
export function defaultDue(now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toInputValue(d);
}

export function emptyFollowUp(name, now = new Date()) {
  return {
    enabled: false,
    title: name ? `Follow up with ${name}` : "Follow up on call",
    due: defaultDue(now),
    reminder: "15m",
    assignedTo: null,
  };
}

/** ISO instant for the reminder, or null. `due` is a datetime-local string. */
export function reminderAtFor(due, choice) {
  if (!due || !choice || choice === "none" || !(choice in OFFSET_MS)) return null;
  const local = new Date(due); // datetime-local strings parse as local time
  if (Number.isNaN(local.getTime())) return null;
  return new Date(local.getTime() - OFFSET_MS[choice]).toISOString();
}

/** The record a call is about: what the server linked, else what the console resolved. */
export function callParent(activeCall, resolved) {
  if (activeCall?.primary_parent_table && activeCall?.primary_parent_id) {
    return { table: activeCall.primary_parent_table, id: activeCall.primary_parent_id };
  }
  const table = resolved ? PARENT_TABLES[resolved.type] : null;
  return table && resolved?.id ? { table, id: resolved.id } : null;
}

/** Problems that stop the follow-up being saved, as a message (or ""). */
export function followUpProblem(followUp, parent, now = new Date()) {
  if (!followUp?.enabled) return "";
  if (!parent) return "Choose who this call was about to add a follow-up.";
  if (!followUp.title?.trim()) return "Give the follow-up a title.";
  const due = new Date(followUp.due);
  if (!followUp.due || Number.isNaN(due.getTime())) return "Set when the follow-up is due.";
  if (due.getTime() < now.getTime() - 60e3) return "The follow-up cannot be due in the past.";
  return "";
}

/** POST /activities/ body for the follow-up task. */
export function followUpActivity(followUp, parent, { userId, callNotes } = {}) {
  return {
    title: followUp.title.trim(),
    type: "Task",
    status: "Scheduled",
    description: callNotes ? `From the call: ${callNotes}` : null,
    due_date: toLocalDateTimeApi(followUp.due),
    reminder_at: reminderAtFor(followUp.due, followUp.reminder),
    parent_table: parent.table,
    parent_table_id: parent.id,
    assigned_to: followUp.assignedTo || userId || null,
  };
}
